"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { getWithAuth, postWithAuth } from "@/lib/api-helpers";
import ToggleSetting from "../components/ToggleSetting";

interface EmailPrefs {
  enabled: boolean;
  mentions: boolean;
  comments: boolean;
  pageShares: boolean;
  permissionChanges: boolean;
  assignments: boolean;
  workspaceInvites: boolean;
  workareaAccess: boolean;
  accessRequests: boolean;
  reminders: boolean;
}

interface NotificationPreferences {
  email: EmailPrefs;
}

const DEFAULTS: NotificationPreferences = {
  email: {
    enabled: true,
    mentions: true,
    comments: true,
    pageShares: true,
    permissionChanges: true,
    assignments: true,
    workspaceInvites: true,
    workareaAccess: true,
    accessRequests: true,
    reminders: true,
  },
};

export default function NotificationsContent() {
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULTS);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  // Load current preferences
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getWithAuth("/api/user/notification-preferences");
        if (cancelled) return;
        if (res?.preferences) {
          setPrefs({
            email: { ...DEFAULTS.email, ...(res.preferences.email || {}) },
          });
        }
      } catch (err) {
        console.error("Failed to load notification preferences:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Optimistic update + server persist
  const update = async (patch: Partial<EmailPrefs>) => {
    const next: NotificationPreferences = {
      email: { ...prefs.email, ...patch },
    };
    setPrefs(next);
    setSaving(true);
    try {
      const res: any = await postWithAuth("/api/user/notification-preferences", {
        preferences: { email: patch },
      });
      if (res?.preferences) {
        setPrefs({
          email: { ...DEFAULTS.email, ...(res.preferences.email || {}) },
        });
      }
    } catch (err) {
      console.error("Failed to update notification preferences:", err);
      toast.error("Could not save your preference. Please try again.");
      // Roll back
      setPrefs(prefs);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500 dark:text-zinc-400" />
      </div>
    );
  }

  const masterOn = prefs.email.enabled;

  return (
    <div className="space-y-12">
      <div>
        <div className="text-zinc-900 dark:text-zinc-100 text-lg font-semibold">
          Notifications
        </div>
        <div className="text-zinc-600 dark:text-zinc-400 text-sm mt-1">
          Decide when and how you want to be notified
        </div>
      </div>

      {/* Email notifications */}
      <section className="space-y-6">
        <div className="border-b border-component pb-3 text-base font-medium text-zinc-900 dark:text-zinc-100 flex items-center justify-between">
          <span>Email notifications</span>
          {saving && (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />
          )}
        </div>

        <ToggleSetting
          label="Activity in my workspace"
          description="Get emails about comments, mentions, page invites, access requests, permission changes, and database property changes."
          checked={prefs.email.enabled}
          onChange={(checked) => update({ enabled: checked })}
        />

        {/* Granular toggles — disabled when the master switch is off */}
        <div
          className={
            masterOn
              ? "space-y-6"
              : "space-y-6 opacity-50 pointer-events-none"
          }
          aria-disabled={!masterOn}
        >
          <ToggleSetting
            label="Mentions"
            description="When someone @-mentions you in a note or a comment."
            checked={prefs.email.mentions}
            onChange={(checked) => update({ mentions: checked })}
          />
          <ToggleSetting
            label="Reminders"
            description="When a reminder you set on a date comes due."
            checked={prefs.email.reminders}
            onChange={(checked) => update({ reminders: checked })}
          />
          <ToggleSetting
            label="Comments"
            description="New comments and replies on notes you created or have replied to."
            checked={prefs.email.comments}
            onChange={(checked) => update({ comments: checked })}
          />
          <ToggleSetting
            label="Page shares"
            description="When a note is shared with you."
            checked={prefs.email.pageShares}
            onChange={(checked) => update({ pageShares: checked })}
          />
          <ToggleSetting
            label="Permission changes"
            description="When your role on a note is updated."
            checked={prefs.email.permissionChanges}
            onChange={(checked) => update({ permissionChanges: checked })}
          />
          <ToggleSetting
            label="Assignments"
            description="When you're assigned to a row in a database."
            checked={prefs.email.assignments}
            onChange={(checked) => update({ assignments: checked })}
          />
          <ToggleSetting
            label="Workspace invitations"
            description="When you're added to a workspace."
            checked={prefs.email.workspaceInvites}
            onChange={(checked) => update({ workspaceInvites: checked })}
          />
          <ToggleSetting
            label="Workarea access"
            description="When you're granted access to a workarea."
            checked={prefs.email.workareaAccess}
            onChange={(checked) => update({ workareaAccess: checked })}
          />
          <ToggleSetting
            label="Access requests"
            description="Workspace and workarea join requests, plus accept/reject decisions."
            checked={prefs.email.accessRequests}
            onChange={(checked) => update({ accessRequests: checked })}
          />
        </div>
      </section>
    </div>
  );
}
